import { useEffect, useId, useRef, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";

/** onConfirm must return a promise and reject on failure. Cancellation is locked while pending. */
export default function ConfirmDialog({
  open,
  title,
  description = "This action cannot be undone.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  loading = false,
  onConfirm,
  onCancel,
  confirmationValue,
}) {
  const id = useId();
  const busy = useRef(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (open) {
      setError(false);
      setTyped("");
    }
  }, [open]);
  const processing = pending || loading;
  const cancel = () => {
    if (!busy.current && !loading) onCancel();
  };
  const confirm = async () => {
    if (
      busy.current ||
      loading ||
      (confirmationValue && typed !== confirmationValue)
    )
      return;
    busy.current = true;
    setPending(true);
    setError(false);
    try {
      await onConfirm();
      onCancel();
    } catch {
      setError(true);
    } finally {
      busy.current = false;
      setPending(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={cancel}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      maxWidth="xs"
    >
      <DialogTitle id={`${id}-title`}>{title}</DialogTitle>
      <DialogContent aria-busy={processing}>
        <DialogContentText id={`${id}-description`}>
          {description}
        </DialogContentText>
        {confirmationValue && (
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label={`Type ${confirmationValue} to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={processing}
          />
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Unable to complete this action. Please try again.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={cancel} disabled={processing}>
          {cancelText}
        </Button>
        <Button
          variant="contained"
          color={
            {
              danger: "error",
              warning: "warning",
              success: "success",
              default: "primary",
            }[variant] || "primary"
          }
          onClick={confirm}
          disabled={
            processing ||
            Boolean(confirmationValue && typed !== confirmationValue)
          }
          startIcon={
            processing ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {processing ? "Processing…" : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
